import Vue, { Component, AsyncComponent } from "vue";
import VueI18n from 'vue-i18n'
import { NewContract, ValidationErrors, PhaseDescriptor, INewContractOptions } from "../../new-contract-vue"
import { NewContractEvents } from "../../new-contract-vue-events";

declare let selectedProductId: number;
declare let selectedPriceListId: number;
declare let selectedCustomerType: number;
declare let selectedTarification: number;
declare let selectedHousingType: number;
declare let campaignCode: string;
declare let currentLocale: string;
declare let membership: string;
declare let brand: string;
declare let offerRequest: boolean;

const isEnglish = currentLocale?.startsWith("en");
const isSwedish = currentLocale?.startsWith("sv");
const isFinnish = !isEnglish && !isSwedish || (currentLocale == null || currentLocale == "" || currentLocale.startsWith("fi")) ? true : false;

Vue.use(VueI18n);
const i18n = new VueI18n({
    locale: currentLocale,
    fallbackRoot: true,
    silentTranslationWarn: true
});

export abstract class ContractPageBase {
    protected newContract: NewContract;

    constructor(
        rootSelector: string,
        phases: PhaseDescriptor[],
        components: { [key: string]: Component<any, any, any, any> | AsyncComponent<any, any, any, any> },
        initialPhaseId?: number,
        autoClearSelectedProduct?: boolean, 
        offerRequest?: boolean,
  
    ) {
        const options = {
            el: rootSelector,
            phases: phases,
            preselects: {
                productId: selectedProductId,
                priceListId: selectedPriceListId,
                customerType: selectedCustomerType,
                tarification: selectedTarification,
                housingType: selectedHousingType,
                campaignCode: campaignCode,
                membership: membership,
                brand: brand,
                offerRequest: offerRequest,
            },
            // Add Vue stuff specific to default.
            mixin: {
                components: components,
                i18n,
                mounted: () => this.onPageMounted(),
                beforeDestroy: () => this.onPageUnmounted()
            },
            initialPhaseId: initialPhaseId,
            autoClearSelectedProduct: autoClearSelectedProduct
        } as INewContractOptions;

        this.newContract = new NewContract(options);
    }

    protected onPageMounted(): void {
        console.log("Page mounted!");
        NewContractEvents.addValidationFailedListener(this.validationFailedListener);
        NewContractEvents.addProductSelectionClearedListener(this.productSelectionClearedListener);
    }

    protected onPageUnmounted(): void {
        console.log("Page unmounted!");
        NewContractEvents.removeValidationFailedListener(this.validationFailedListener);
        NewContractEvents.removeProductSelectionClearedListener(this.productSelectionClearedListener);
    }

    // With this extra declaration we can declare validationFailedListener, which calls validationFailed method
    // with 'this' context being instance of ContractPage. If validationFailed was used directly as event handler, it's
    // 'this' would be something else, and it would not be possible to access ContractPage instance properties.
    validationFailedListener = (errors) => this.validationFailed(errors);

    validationFailed(validationErrors: ValidationErrors) {
        const errorMessages = validationErrors.getAll();
        const errors = [];

        const h = this.newContract.vue.$createElement;
        
        for (let i = 0; i < errorMessages.length; ++i) {
            errors.push(h("li", errorMessages[i]));
        }

        const vNodesMsg = h(
            'ul',
            { class: ['m-2'] },
            errors
        );

        let titleTranslation = "Tarkista tiedot";

        if (isEnglish) {
            titleTranslation = "Correct the mentioned fields";
        }
        else if (isSwedish) {
            titleTranslation = "Kolla informationen";
        }

        const vNodesTitle = h(
            'div',
            {
                class: ["m-1"]
            },
            titleTranslation
        );

        this.newContract.vue.$bvToast.toast([vNodesMsg], {
            title: [vNodesTitle],
            variant: "danger",
            solid: true,
            autoHideDelay: 10000
        });
    }

    productSelectionClearedListener = () => this.productSelectionCleared();

    productSelectionCleared() {
        let messageTranslation = "Valittavissa olevat tuotteet muuttuivat";
        let titleTranslation = "Tuotteet muuttuneet";

        if (isEnglish) {
            messageTranslation = "Your actions have changed the products you can choose from";
            titleTranslation = "Products have been changed";
        }
        else if (isSwedish) {
            messageTranslation = "De tillgängliga produkterna har ändrats";
            titleTranslation = "Produkterna har ändrats";
        }

        this.newContract.vue.$bvToast.toast(messageTranslation, {
            title: titleTranslation,
            variant: "warning",
            solid: true,
            autoHideDelay: 10000
        });
    }
}